/**
 * Pharmacy arithmetic and vocabulary.
 *
 * Quantities are whole numbers of a product's BASE unit — the tablet, the
 * sachet, the bottle. A selling unit (strip, pack, carton) holds a whole
 * number of base units, so "three strips" is always an integer number of
 * tablets and no fraction of an indivisible unit can be expressed at all.
 *
 * Money is exact. Prices and totals are 2-decimal strings handled as bigint
 * minor units (see money.ts). Cost per base unit is held to 6 decimals,
 * because a pack of 30 bought at 1,250.00 costs 41.666667 a tablet, and
 * rounding that to kobo on the batch would misstate every sale drawn from it.
 * Rounding happens once, half-up, when a quantity is costed into a money
 * amount.
 */

import { toDecimalString, toMinor } from './money.js';

export const RX_CLASSES = ['otc', 'pharmacy_only', 'prescription_only', 'restricted'] as const;
export type RxClass = (typeof RX_CLASSES)[number];
export const RX_CLASS_LABELS: Record<RxClass, string> = {
  otc: 'Over the counter',
  pharmacy_only: 'Pharmacy only (pharmacist present)',
  prescription_only: 'Prescription only',
  restricted: 'Restricted / controlled',
};

export const STORAGE_REQUIREMENTS = ['room', 'cool', 'refrigerated', 'frozen'] as const;
export type StorageRequirement = (typeof STORAGE_REQUIREMENTS)[number];
export const STORAGE_REQUIREMENT_LABELS: Record<StorageRequirement, string> = {
  room: 'Room temperature',
  cool: 'Cool, dry place',
  refrigerated: 'Refrigerated (2–8 °C)',
  frozen: 'Frozen',
};

export const ADJUSTMENT_REASONS = [
  'damaged',
  'expired',
  'write_off',
  'count_correction',
  'theft_loss',
  'found',
  'disposal',
  'supplier_return',
  'sample',
  'other',
] as const;
export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];
export const ADJUSTMENT_REASON_LABELS: Record<AdjustmentReason, string> = {
  damaged: 'Damaged',
  expired: 'Expired',
  write_off: 'Write-off',
  count_correction: 'Count correction',
  theft_loss: 'Theft or loss',
  found: 'Found stock',
  disposal: 'Disposal',
  supplier_return: 'Returned to supplier',
  sample: 'Sample',
  other: 'Other',
};

/** Reasons whose loss is reported as expiry/damage loss. */
export const LOSS_REASONS: readonly AdjustmentReason[] = [
  'damaged',
  'expired',
  'write_off',
  'theft_loss',
  'disposal',
];

export const REFUND_DISPOSITIONS = ['restock', 'dispose', 'return_to_supplier'] as const;
export type RefundDisposition = (typeof REFUND_DISPOSITIONS)[number];

// ── Exact cost arithmetic ──────────────────────────────────────────────────

const MICRO = 1_000_000n;

/** A decimal string with up to 6 places, as integer millionths. */
export function toMicro(value: string | number): bigint {
  const raw = String(value).trim();
  if (!/^-?\d+(\.\d+)?$/.test(raw)) throw new Error(`Not a decimal: ${raw}`);
  const negative = raw.startsWith('-');
  const [whole = '0', frac = ''] = (negative ? raw.slice(1) : raw).split('.');
  // Beyond 6 places, round half-up rather than truncate.
  const head = frac.padEnd(6, '0').slice(0, 6);
  let micro = BigInt(whole) * MICRO + BigInt(head);
  if (frac.length > 6 && Number(frac[6]) >= 5) micro += 1n;
  return negative ? -micro : micro;
}

export function microToString(micro: bigint): string {
  const negative = micro < 0n;
  const abs = negative ? -micro : micro;
  return `${negative ? '-' : ''}${abs / MICRO}.${(abs % MICRO).toString().padStart(6, '0')}`;
}

/** Divide, rounding half away from zero. */
function divRound(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new Error('Division by zero');
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const q = (n * 2n + d) / (2n * d);
  return negative ? -q : q;
}

/**
 * Cost per base unit from what one purchase unit cost.
 * 1,250.00 a pack of 30 → "41.666667".
 */
export function costPerBaseUnit(unitCost: string, baseQuantity: number): string {
  if (!Number.isInteger(baseQuantity) || baseQuantity <= 0) {
    throw new Error('A unit holds a positive whole number of base units');
  }
  return microToString(divRound(toMicro(unitCost), BigInt(baseQuantity)));
}

/**
 * What `quantity` base units cost, in money: rounded half-up to 2 places,
 * once. 97 tablets at 41.666667 → "4041.67".
 */
export function costOf(quantity: number, unitCostPerBase: string): string {
  if (!Number.isInteger(quantity)) throw new Error('Quantities are whole base units');
  const micro = toMicro(unitCostPerBase) * BigInt(quantity);
  return toDecimalString(divRound(micro, 10_000n));
}

/** price × count, exact. */
export function multiplyPrice(price: string, count: number): string {
  if (!Number.isInteger(count)) throw new Error('Counts are whole numbers');
  return toDecimalString(toMinor(price) * BigInt(count));
}

/**
 * Base units in `count` of a unit that holds `baseQuantity`.
 * Refuses anything that is not a positive whole number of units.
 */
export function toBaseUnits(count: number, baseQuantity: number): number {
  if (!Number.isInteger(count) || count <= 0) {
    throw new Error('Sell and receive whole units only');
  }
  if (!Number.isInteger(baseQuantity) || baseQuantity <= 0) {
    throw new Error('A unit holds a positive whole number of base units');
  }
  const total = count * baseQuantity;
  if (!Number.isSafeInteger(total)) throw new Error('Quantity too large');
  return total;
}

/**
 * Split a basket discount across lines in proportion to their totals, so the
 * parts add up to the whole exactly — the last line takes the remainder.
 */
export function apportion(total: string, weights: readonly string[]): string[] {
  const whole = toMinor(total);
  const w = weights.map((x) => toMinor(x));
  const sum = w.reduce((a, b) => a + b, 0n);
  if (sum === 0n) return weights.map(() => '0.00');
  const parts: bigint[] = [];
  let given = 0n;
  w.forEach((weight, i) => {
    if (i === w.length - 1) {
      parts.push(whole - given);
    } else {
      const part = (whole * weight) / sum;
      parts.push(part);
      given += part;
    }
  });
  return parts.map(toDecimalString);
}

// ── Loyalty points (0159) ─────────────────────────────────────────────────

/** Whole points earned on `value`, at one point per `spendPerPoint`, rounded down. */
export function loyaltyPointsFor(value: string, spendPerPoint: string): number {
  const v = toMinor(value);
  const per = toMinor(spendPerPoint);
  if (v <= 0n || per <= 0n) return 0;
  return Number(v / per);
}

/** What `points` are worth at `pointValue` each. */
export function loyaltyValueOf(points: number, pointValue: string): string {
  return toDecimalString(BigInt(points) * toMinor(pointValue));
}

/**
 * Points given back when lines bought partly with points are refunded: each
 * line's share of the points discount, in proportion to the quantity
 * returned, at the value a point had when it was spent — rounded down, so a
 * full refund gives back exactly what was spent and a partial one never more.
 */
export function loyaltyPointsRestored(
  lines: readonly { share: string; sold: number; refunded: number }[],
  pointValue: string,
): number {
  const pv = toMinor(pointValue);
  if (pv <= 0n) return 0;
  const live = lines.filter((l) => l.sold > 0 && toMinor(l.share) > 0n);
  if (!live.length) return 0;
  // Σ share × refunded / sold, kept exact over a common denominator.
  const denominator = live.reduce((d, l) => d * BigInt(l.sold), 1n);
  const numerator = live.reduce(
    (n, l) => n + toMinor(l.share) * BigInt(Math.min(l.refunded, l.sold)) * (denominator / BigInt(l.sold)),
    0n,
  );
  return Number(numerator / (denominator * pv));
}
